import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { useTheme } from "@/theme/ThemeProvider";
import { CheckIcon, XIcon } from "phosphor-react-native";
import React, { useEffect, useRef, useState } from "react";
import type { TextInput } from "react-native";
import { Pressable, View } from "react-native";

type TreeNodeInlineInputProps = {
  depth: number;
  placeholder: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
  autoFocus?: boolean;
};

export function TreeNodeInlineInput({
  depth,
  placeholder,
  onConfirm,
  onCancel,
  autoFocus = true,
}: TreeNodeInlineInputProps) {
  const { theme } = useTheme();
  const [value, setValue] = useState("");
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (autoFocus) {
      // Small delay to ensure component is mounted
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const handleConfirm = () => {
    const trimmed = value.trim();
    if (trimmed) {
      onConfirm(trimmed);
      setValue("");
    }
  };

  const handleKeyPress = (e: any) => {
    if (e.nativeEvent.key === "Enter") {
      handleConfirm();
    } else if (e.nativeEvent.key === "Escape") {
      onCancel();
    }
  };

  const canConfirm = value.trim().length > 0;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        marginLeft: depth * theme.space.lg,
        paddingVertical: theme.space.xs,
        paddingRight: theme.space.sm,
        gap: theme.space.sm,
      }}
    >
      <FloatingLabelInput
        ref={inputRef}
        label={placeholder}
        value={value}
        onChangeText={setValue}
        onKeyPress={handleKeyPress}
        onBlur={() => {
          if (!value.trim()) {
            onCancel();
          }
        }}
        containerStyle={{ flex: 1 }}
      />

      <Pressable
        onPress={handleConfirm}
        disabled={!canConfirm}
        style={{
          padding: theme.space.sm,
          borderRadius: theme.radius.md,
          backgroundColor: canConfirm
            ? theme.color.primary
            : theme.color.surfaceStrong,
        }}
      >
        <CheckIcon
          size={18}
          color={canConfirm ? theme.color.buttonText : theme.color.muted}
          weight="bold"
        />
      </Pressable>

      <Pressable
        onPress={onCancel}
        style={{
          padding: theme.space.sm,
          borderRadius: theme.radius.md,
          backgroundColor: theme.color.surfaceStrong,
        }}
      >
        <XIcon size={18} color={theme.color.muted} weight="bold" />
      </Pressable>
    </View>
  );
}
